import { useTranslations } from "next-intl";

/**
 * Minimal translator shape used by `formatLedgerFieldLabel`. Structurally
 * compatible with the function returned by `useTranslations("ledger.fields")`
 * — we only use the call signature and the `.has()` method, so we avoid
 * pulling in next-intl's narrow key types and keep the helper testable in
 * isolation (no React render, no DOM, no @testing-library/react).
 */
type LedgerFieldTranslator = {
  (key: string): string;
  has: (key: string) => boolean;
};

/**
 * Pure helper: resolves a DYNAMIC, backend-driven `ledger.fields.*` key to its
 * translated label, returning `""` when the key is empty/missing.
 *
 * The ledger detail components build these keys from a `reason` object whose
 * fields can be empty/undefined (e.g. `reasonTranslation` is only set on rebate
 * reasons). Calling `t("")` or `t(unknownKey)` makes next-intl fire its
 * `onError` callback, which Sentry records as `MISSING_MESSAGE: ledger.fields.`
 * (CUSTOMER-UI-6M — the title is literally truncated at the dot). A trailing
 * `|| ""` does NOT prevent this: `onError` fires BEFORE `t()` returns the
 * fallback. Short-circuiting on `.has()` means `t()` is never invoked for a
 * missing/empty key, so no `onError`, no Sentry event.
 *
 * Returns `""` (not the raw token) because these keys are internal field-label
 * identifiers like `amountSent` — surfacing the raw token would be wrong, and
 * `""` lets `<DetailRow>`'s `if (!value) return null` drop the row cleanly.
 * STATIC-literal call sites (`t("status")`, `t("network")`, …) are always
 * present in the dictionary and should keep calling `t` directly.
 */
export function formatLedgerFieldLabel(
  key: string | null | undefined,
  t: LedgerFieldTranslator
): string {
  return key && t.has(key) ? t(key) : "";
}

/**
 * Hook wrapper bound to the `ledger.fields` namespace. Returns a guard that
 * routes DYNAMIC keys through `formatLedgerFieldLabel`.
 */
export function useLedgerFieldLabel() {
  const tLedgerFields = useTranslations("ledger.fields");
  return (key: string | null | undefined): string =>
    formatLedgerFieldLabel(key, tLedgerFields as LedgerFieldTranslator);
}
